import { useEffect, useState } from 'react'
import { message } from 'antd'
import { useParams } from 'react-router-dom'
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox'

import { deleteUserNotesMutation, resolveUserNoteMutation, saveNoteMutation } from 'gql/user'
import { commonHelper } from 'helpers'
import { INote, INewNote } from 'pages/ClientDetails/DetailedForm/Forms/Notes/Notes.interface'
import { useGetUserNotes, useExportUserNotes } from 'services/users/users.service'
import { useCustomMutation } from 'services/shared/mutation'
import { DeleteUserNotesPayload, ResolveUserNotePayload, SaveUserNotePayload } from 'services/shared/mutation.payload'
import { eventManager } from 'utils'

const currentDate = new Date().toISOString().split('T')[0]

const useUserNotes = () => {
  const { userId } = useParams()
  const deleteNotes = useCustomMutation<DeleteUserNotesPayload>({ mutation: deleteUserNotesMutation, refetchQueries: ['getUserNotes'] })
  const resolveNote = useCustomMutation<ResolveUserNotePayload>({ mutation: resolveUserNoteMutation, refetchQueries: ['getUserNotes', 'getAgentNotes'] })
  const handleSaveNote = useCustomMutation<SaveUserNotePayload>({
    mutation: saveNoteMutation,
    refetchQueries: ['getUserNotes'],
  })
  const { notes: initialNotes, loading, refetch } = useGetUserNotes(userId as string)
  const { refetch: exportRefetch } = useExportUserNotes(userId as string)
  const [addingNewNote, setAddingNewNote] = useState(false)
  const [selectAllNotes, setSelectAllNotes] = useState(false)
  const [notes, setNotes] = useState<INote[]>([])
  const [newNote, setNewNote] = useState<INewNote>({
    description: '',
    // title: '',
    followUpDate: currentDate,
    createdAt: currentDate,
  })

  const displayEmptyNotesUI = notes.length === 0 && !addingNewNote && !loading

  useEffect(() => {
    setNotes(
      initialNotes.map((note: INote) => {
        const formatDate = (timestamp: number) => {
          const date = new Date(timestamp)
          const year = date.getFullYear()
          const month = (date.getMonth() + 1).toString().padStart(2, '0') // +1 because months are 0-indexed
          const day = date.getDate().toString().padStart(2, '0')
          return `${year}-${month}-${day}`
        }

        const formattedDate = formatDate(Number(note.createdAt))
        const formattedFollowUpDate = formatDate(Number(note.followUpDate))
        return {
          ...note,
          selected: false,
          createdAt: formattedDate,
          followUpDate: formattedFollowUpDate,
          edit: false,
          editable: false,
          delete: false,
          resolvable: false,
        }
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const addNote = () => {
    setAddingNewNote(true)
  }

  const cancelNewItemInsert = () => {
    setAddingNewNote(false)
    setNewNote({
      description: '',
      // title: '',
      followUpDate: '',
      createdAt: currentDate,
    })
  }

  const cancelEdit = (_id: string) => {
    setNotes((prevNotes) => {
      setSelectAllNotes(false)
      refetch()
      const index = prevNotes.findIndex((note) => note._id === _id)
      const newArr = [...prevNotes]
      newArr[index].selected = false
      newArr[index].editable = false
      newArr[index].edit = false
      newArr[index].delete = false
      return newArr
    })
  }

  const selectAllNotesChange = (e: CheckboxChangeEvent) => {
    setSelectAllNotes(e.target.checked)
    setNotes((prevNotes) => {
      const newArr = [...prevNotes].map((note) => ({ ...note, selected: e.target.checked }))
      return newArr
    })
  }

  const selectNote = (e: CheckboxChangeEvent, _id: string) => {
    setNotes((prevNotes) => {
      const index = prevNotes.findIndex((note) => note._id === _id)
      const newArr = [...prevNotes]
      newArr[index].selected = e.target.checked
      newArr[index].edit = e.target.checked
      newArr[index].delete = e.target.checked
      newArr[index].resolvable = e.target.checked

      if (e.target.checked) {
        newArr.every((note) => note.selected) && setSelectAllNotes(true)
      } else {
        newArr.every((note) => !note.selected) && setSelectAllNotes(false)
      }

      return newArr
    })
  }

  const handleCustomTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewNote((prevState: INewNote) => ({
      ...prevState,
      description: e.target.value,
    }))
  }

  const handleCustomFormItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewNote((prevState: INewNote) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  const saveNote = async ({ _id }: { _id: string }) => {
    let notePayload: any = {}

    if (_id) {
      const note = notes.find((note) => note._id === _id)
      notePayload = {
        _id,
        followUpDate: note?.followUpDate,
        description: note?.description,
      }
    } else {
      notePayload = {
        ...newNote,
        _id,
      }
    }

    const { errors } = await handleSaveNote({
      userId: userId as string,
      note: notePayload,
    })
    setAddingNewNote(false)
    setNewNote({
      description: '',
      // title: '',
      followUpDate: '',
      createdAt: currentDate,
    })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
  }

  const handleDelete = async (_id?: string) => {
    let _ids: string[] = []

    if (_id) {
      // if a single note is checked, will remove that one.
      _ids = [_id]
    } else if (!_id && selectAllNotes) {
      // if all notes are checked, will remove all.
      notes.map((note) => _ids.push(note._id))
    }

    const { errors } = await deleteNotes({ _ids, userId: userId as string })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Deleted successfully')
    setSelectAllNotes(false)
  }

  const handleDeleteModal = (_id?: string) => {
    eventManager.openConfirmDeleteModal(
      { header: 'Do you really want to delete? This process can’t be undone.' },
      {
        confirm: () => handleDelete(_id),
      },
    )
  }

  const handleEditClick = (_id: string) => {
    setNotes((prevNotes) => {
      const index = prevNotes.findIndex((note) => note._id === _id)
      const newArr = [...prevNotes]
      newArr[index].editable = true
      return newArr
    })
  }

  const handleResolve = async (_id: string) => {
    const { errors } = await resolveNote({ userId: userId as string, noteId: _id })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Resolved successfully')
  }

  const handleResolveClick = (_id: string) => {
    eventManager.openResolveModal(
      { header: 'Do you really want to Resolve this note?' },
      {
        confirm: () => handleResolve(_id),
      },
    )
  }

  const handleCustomTextAreaChangeForEdit = (e: React.ChangeEvent<HTMLTextAreaElement>, _id: string) => {
    setNotes((prevState: INote[]) => {
      const index = prevState.findIndex((note) => note._id === _id)
      prevState[index].description = e.target.value
      return [...prevState]
    })
  }

  const handleCustomFormItemChangeForEdit = (e: React.ChangeEvent<HTMLInputElement>, _id: string) => {
    setNotes((prevState: INote[]) => {
      const index = prevState.findIndex((note) => note._id === _id)
      prevState[index].followUpDate = e.target.value

      return [...prevState]
    })
  }

  const handleExportNotes = async () => {
    try {
      const result = await exportRefetch({
        userId: userId as string,
      })

      if (result.data?.exportUserNotes) {
        // The backend returns base64-encoded Excel data
        const base64Data = result.data.exportUserNotes

        // Convert base64 to binary more efficiently
        const binaryString = atob(base64Data)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        // Create blob and download
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'user_notes.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Export failed:', error)
      message.error('Failed to export notes')
    }
  }

  return {
    notes,
    addNote,
    displayEmptyNotesUI,
    addingNewNote,
    selectAllNotes,
    selectAllNotesChange,
    selectNote,
    newNote,
    cancelNewItemInsert,
    handleCustomTextAreaChange,
    handleCustomFormItemChange,
    saveNote,
    handleDeleteModal,
    handleEditClick,
    handleCustomTextAreaChangeForEdit,
    handleCustomFormItemChangeForEdit,
    cancelEdit,
    handleResolveClick,
    handleExportNotes,
  }
}

export default useUserNotes
